export type CollectionRoom = "scanned" | "pieces";

export type CollectionItemLike = {
  itemType?: string;
  status?: string;
  isSampleItem?: boolean;
  story?: string;
  lastScannedAt?: string | Date;
  createdAt?: string | Date;
};

export const isSampleItem = (item?: CollectionItemLike | null): boolean =>
  Boolean(item?.isSampleItem);

export const isScannedTrailItem = (item?: CollectionItemLike | null): boolean => {
  if (!item || isSampleItem(item)) {
    return false;
  }
  return (
    String(item.itemType || "").toUpperCase() === "HKP" &&
    String(item.status || "").toUpperCase() === "SCANNED"
  );
};

export const isMyPiecesItem = (item?: CollectionItemLike | null): boolean => {
  if (!item || isSampleItem(item)) {
    return false;
  }
  if (String(item.status || "").toUpperCase() === "OWNED") {
    return true;
  }
  return String(item.itemType || "").toUpperCase() === "EXTERNAL";
};

export const itemRoom = (
  item?: CollectionItemLike | null
): CollectionRoom | "demo" | "unknown" => {
  if (!item) {
    return "unknown";
  }
  if (isSampleItem(item)) {
    return "demo";
  }
  if (isScannedTrailItem(item)) {
    return "scanned";
  }
  if (isMyPiecesItem(item)) {
    return "pieces";
  }
  return "unknown";
};

export const partitionCollectionItems = <T extends CollectionItemLike>(items: T[]) => {
  const scanned: T[] = [];
  const pieces: T[] = [];
  const samples: T[] = [];
  items.forEach((item) => {
    if (isSampleItem(item)) {
      samples.push(item);
      return;
    }
    if (isScannedTrailItem(item)) {
      scanned.push(item);
      return;
    }
    if (isMyPiecesItem(item)) {
      pieces.push(item);
    }
  });
  return { scanned, pieces, samples };
};

export const defaultCollectionPath = <T extends CollectionItemLike>(items: T[]): string => {
  const { scanned, pieces } = partitionCollectionItems(items);
  if (pieces.length === 0 && scanned.length > 0) {
    return "/my-collection/scanned";
  }
  return "/my-collection";
};

export const collectionTitle = (displayName?: string): string => {
  const name = String(displayName || "").trim();
  if (!name) {
    return "My Collection";
  }
  return `${name}'s collection`;
};

export const storyFirstLine = (story?: string): string => {
  const text = String(story || "").replace(/\s+/g, " ").trim();
  if (!text) {
    return "";
  }
  const line = text.split(/[.!?]/)[0].trim() || text;
  return line.length > 90 ? `${line.slice(0, 87).trim()}…` : line;
};

export const sawThisLabel = (iso?: string | Date): string => {
  if (!iso) {
    return "Saw this";
  }
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "Saw this";
  }
  const formatted = date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `Saw this · ${formatted}`;
};

export const firstDemoItem = <T extends CollectionItemLike>(samples: T[]): T | undefined => {
  return samples.find((item) => String(item.itemType || "").toUpperCase() === "EXTERNAL") || samples[0];
};

/** Newest piece that still has no story, so home can ask for it; null when every piece has one. */
export const latestPieceWithoutStory = <T extends { story?: string; createdAt?: string }>(pieces: T[]): T | null => {
  const open = pieces.filter((p) => !String(p.story || "").trim());
  if (!open.length) return null;
  return open.reduce((a, b) => (String(b.createdAt || "") > String(a.createdAt || "") ? b : a));
};
